import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Modal from '../../components/ui/Modal';
import { CheckCircle, XCircle, ArrowUp, Search, Eye, Send } from 'lucide-react';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import LiveBadge from '../../components/LiveBadge';

const STATUSES = ['pending', 'approved', 'rejected', 'all'];

export default function WithdrawalRequestsPage() {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialStatus = STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : 'pending';
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState('week');
  const [kpis, setKpis] = useState(null);

  useEffect(() => { fetchRequests(); }, [status, search]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/admin/transactions/kpis/requests?type=withdrawal&period=${kpiPeriod}`);
        setKpis(res.data);
      } catch { /* non-fatal */ }
    })();
  }, [kpiPeriod]);

  // Live auto-sync every 5s for new withdrawal requests (silent — no blink).
  useAutoRefresh(async () => {
    fetchRequests({ silent: true });
    try {
      const res = await api.get(`/api/admin/transactions/kpis/requests?type=withdrawal&period=${kpiPeriod}`);
      setKpis(res.data);
    } catch { /* non-fatal */ }
  }, 5000, !selected);

  const fetchRequests = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    try {
      let url = `/api/admin/transactions/withdrawals?limit=100&status=${status === 'all' ? '' : status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await api.get(url);
      setRequests(res.data.items || []);
    } catch { if (!silent) toast.error('Failed to load withdrawals'); }
    finally { if (!silent) setLoading(false); }
  };

  const handleApprove = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/approve`, { admin_notes: adminNotes });
      toast.success('Withdrawal approved!');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const handleReject = async (id) => {
    setProcessing(true);
    try {
      await api.post(`/api/admin/transactions/${id}/reject`, { reason: adminNotes });
      toast.success('Withdrawal rejected');
      setSelected(null); setAdminNotes(''); fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const [resendingId, setResendingId] = useState(null);
  const handleResend = async (id) => {
    setResendingId(id);
    try {
      const res = await api.post(`/api/admin/bot-approvals/resend/${id}`);
      const botType = res?.data?.result?.bot_type || 'bot';
      toast.success(`Resent to Telegram (${botType.replace('_', ' ')} bot)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed — configure the Cash Out bot first in Approval Bots');
    } finally { setResendingId(null); }
  };

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="withdrawal-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold flex items-center gap-2"><ArrowUp className="w-5 h-5 text-red-400" /> Withdrawal Requests <LiveBadge />
          {requests.filter(r => r.status === 'pending').length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">{requests.filter(r => r.status === 'pending').length} pending</span>}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">KPI period:</span>
          {['today', 'yesterday', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setKpiPeriod(p)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                kpiPeriod === p ? 'bg-[#D4AF37] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`withdrawal-kpi-period-${p}`}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'week' ? 'This Week' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="withdrawal-kpi-row">
        <MiniKPI label="Pending" value={(kpis?.pending_count ?? 0).toLocaleString()} hint={`$${Number(kpis?.pending_amount_usd ?? kpis?.pending_amount_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})} awaiting`} color="#f59e0b" />
        <MiniKPI label="Approved" value={(kpis?.approved_count ?? 0).toLocaleString()} hint="in period" color="#22c55e" />
        <MiniKPI label="Approved Total" value={`$${Number(kpis?.approved_amount_usd ?? kpis?.approved_amount_sc ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}`} hint="in period" color="#D4AF37" />
        <MiniKPI label="Rejected" value={(kpis?.rejected_count ?? 0).toLocaleString()} hint="in period" color="#ef4444" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-lg p-0.5 bg-[#0B0E14]">
          {STATUSES.map(s => <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition ${status === s ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>{s}</button>)}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by username or email..." className="pl-10 bg-[#0B0E14] border-white/10" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-8"><div className="spinner" /></div> : requests.length === 0 ? <p className="text-gray-500 py-8 text-center">No withdrawal requests</p> : (
        <div className="space-y-2">
          {requests.map(req => (
            <div key={req.id} className="glass-card rounded-xl p-4 flex items-center justify-between" data-testid={`withdrawal-${req.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0"><ArrowUp className="w-5 h-5 text-red-400" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-white">{req.username || 'Unknown'}</p>
                  <p className="text-[10px] text-gray-500">{req.email} | {req.payment_method_name || 'N/A'} | {new Date(req.created_at).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-3">
                <div className="text-right">
                  <p className="text-lg font-bold text-red-400" data-testid={`withdrawal-amount-${req.id}`}>
                    ${Number(req.amount || 0).toFixed(2)}
                  </p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${req.status === 'approved' ? 'bg-green-500/20 text-green-400' : req.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{req.status}</span>
                </div>
                {req.status === 'pending' && (
                  <div className="flex gap-2 ml-2">
                    <button onClick={() => { setSelected(req); setAdminNotes(''); }} className="px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium flex items-center gap-1 hover:bg-blue-500/20 transition">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <button onClick={() => handleApprove(req.id)} className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-medium flex items-center gap-1 hover:bg-green-500/20 transition">
                      <CheckCircle className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => handleReject(req.id)} className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-medium flex items-center gap-1 hover:bg-red-500/20 transition">
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => handleResend(req.id)}
                      disabled={resendingId === req.id}
                      className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-medium flex items-center gap-1 hover:bg-cyan-500/20 transition disabled:opacity-50"
                      data-testid={`resend-withdrawal-${req.id}`}
                      title="Re-send this request to the Cash Out bot on Telegram"
                    >
                      <Send className={`w-3.5 h-3.5 ${resendingId === req.id ? 'animate-pulse' : ''}`} />
                      {resendingId === req.id ? 'Sending…' : 'Resend'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Withdrawal Details" maxWidth="max-w-md">
        {selected && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[10px] text-gray-500">User</p><p className="font-medium">{selected.username}</p></div>
              <div><p className="text-[10px] text-gray-500">Amount</p><p className="font-bold text-red-400">${Number(selected.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p></div>
              <div><p className="text-[10px] text-gray-500">Email</p><p>{selected.email}</p></div>
              <div><p className="text-[10px] text-gray-500">Method</p><p>{selected.payment_method_name || 'N/A'}</p></div>
            </div>
            {selected.payout_details && <div><p className="text-[10px] text-gray-500">Payout Details</p><pre className="text-xs bg-[#0B0E14] p-2 rounded-lg">{typeof selected.payout_details === 'string' ? selected.payout_details : JSON.stringify(selected.payout_details, null, 2)}</pre></div>}
            {/* iter 128: Player's payment QR tag — admin scans to send funds. */}
            {selected.qr_image_url && (
              <div data-testid="withdrawal-qr-preview">
                <p className="text-[10px] text-gray-500 mb-1">Player's Payment QR (tap to open full-size)</p>
                <a
                  href={selected.qr_image_url?.startsWith('http')
                    ? selected.qr_image_url
                    : `${process.env.REACT_APP_BACKEND_URL || ''}${selected.qr_image_url}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="withdrawal-qr-link"
                >
                  <img
                    src={selected.qr_image_url?.startsWith('http')
                      ? selected.qr_image_url
                      : `${process.env.REACT_APP_BACKEND_URL || ''}${selected.qr_image_url}`}
                    alt="Player Payment QR"
                    className="w-full rounded-lg max-h-72 object-contain bg-black/30"
                    data-testid="withdrawal-qr-image"
                  />
                </a>
              </div>
            )}
            {selected.status === 'pending' && <>
              <Input value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Admin notes" className="bg-[#0B0E14] border-white/10" />
              <div className="flex gap-2">
                <Button onClick={() => handleApprove(selected.id)} disabled={processing} className="flex-1 bg-green-600">Approve</Button>
                <Button onClick={() => handleReject(selected.id)} disabled={processing} className="flex-1 bg-red-600">Reject</Button>
              </div>
            </>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function MiniKPI({ label, value, hint = null, color = '#D4AF37' }) {
  return (
    <div
      className="rounded-xl p-3 bg-[#131822] border"
      style={{ borderColor: `${color}33` }}
    >
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className="text-xl font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5 truncate">{hint}</div>}
    </div>
  );
}
